/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ReactNode } from "react";
import React, { Fragment } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  CpuChipIcon,
  FolderIcon,
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { twMerge as tw } from "tailwind-merge";

import type { AdminRole } from "~/api/admins";
import { ADMIN_ROLES } from "~/api/admins";
import { useTheme } from "~/providers/ThemeProvider";
import { ROUTES } from "~/router";
import { useUserStore } from "~/stores";
import {
  Cog6ToothIcon,
  DashboardIcon,
  LogoutIcon,
  PeopleIcon,
  PersonCircleIcon,
  PersonIcon,
  XMarkIcon,
} from "~/ui";

export interface SidebarProps {
  show: boolean;
  onClose: () => void;
}

interface SidebarTab {
  name: string;
  href: string;
  icon: (props: any) => ReactNode;
}

export const Sidebar = ({ show, onClose }: SidebarProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const current = location.pathname;
  const { theme, toggleTheme } = useTheme();

  const adminRole = useUserStore((s) => s.admin?.role);
  const logout = useUserStore((s) => s.logout);

  const tabs: Record<string, SidebarTab> = {
    dashboard: {
      name: t("dashboard.dashboard"),
      href: ROUTES.dashboard,
      icon: DashboardIcon,
    },
    admins: {
      name: t("regular_admins.regular_admins"),
      href: ROUTES.regularAdmins,
      icon: PersonIcon,
    },
    patients: {
      name: t("patients.patients"),
      href: ROUTES.patients,
      icon: PersonCircleIcon,
    },
    units: { name: t("units.units"), href: ROUTES.units, icon: PeopleIcon },
    insoles: {
      name: t("insoles.insoles"),
      href: ROUTES.insoles,
      icon: Cog6ToothIcon,
    },
    fileManager: {
      name: t("file_manager.file_manager"),
      href: ROUTES.fileManager,
      icon: FolderIcon,
    },
    firmwares: {
      name: t("firmware.firmwares", "Firmwares"),
      href: ROUTES.firmwares,
      icon: CpuChipIcon,
    },
  };

  const navigationMap: Record<AdminRole, SidebarTab[]> = {
    [ADMIN_ROLES.REGULAR_ADMINISTRATOR]: [
      tabs.patients,
      tabs.units,
      tabs.insoles,
      tabs.fileManager,
      tabs.firmwares,
    ],
    [ADMIN_ROLES.SUPER_ADMINISTRATOR]: [
      tabs.patients,
      tabs.units,
      tabs.insoles,
      tabs.fileManager,
      tabs.firmwares,
    ],
    [ADMIN_ROLES.INVIZA_SUPER_ADMINISTRATOR]: [
      tabs.dashboard,
      tabs.insoles,
      tabs.firmwares,
    ],
  };

  const navigation = adminRole ? navigationMap[adminRole] : [];

  return (
    <>
      <Transition show={show} as={Fragment}>
        <Dialog as="div" className="relative z-50 xl:hidden" onClose={onClose}>
          <TransitionChild
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </TransitionChild>

          <div className="fixed inset-0 flex">
            <TransitionChild
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1">
                <TransitionChild
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={onClose}
                    >
                      <span className="sr-only">
                        {t("layout.close_sidebar")}
                      </span>

                      <XMarkIcon
                        className="size-6 text-primary"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </TransitionChild>
                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-surface-container-high pb-4 ring-1 ring-primary/10 dark:bg-surface-container-high-dark">
                  <div className="flex h-16 shrink-0 items-center gap-3 px-6 pt-4">
                    <h1 className="text-md font-semibold text-primary">
                      {t("layout.admin_platform")}
                    </h1>
                  </div>

                  <nav className="flex flex-1 flex-col justify-between">
                    <ul className="space-y-1">
                      {navigation.map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            className={tw(
                              "group flex gap-x-3 border-l-3 border-transparent px-6 py-2 text-sm font-semibold leading-6 text-neutral-70 hover:text-primary",
                              current === item.href &&
                                "border-info-70 text-primary",
                            )}
                          >
                            <item.icon
                              className="h-6 w-6 shrink-0"
                              aria-hidden="true"
                            />
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      onClick={logout}
                      className={tw(
                        "group flex gap-x-3 rounded-md px-6 py-2 text-sm font-semibold leading-6 text-neutral-70 hover:text-primary",
                      )}
                    >
                      <LogoutIcon aria-hidden={true} />
                      <span>{t("layout.logout")}</span>
                    </button>
                  </nav>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      {/* Static sidebar for desktop */}
      <div className="hidden xl:fixed xl:inset-y-0 xl:z-10 xl:flex xl:w-64 xl:flex-col 2xl:w-72">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-surface-container-high pb-4 pt-4 dark:bg-surface-container-high-dark">
          <div className="flex h-16 shrink-0 items-center justify-between px-6">
            <h1 className="text-lg font-semibold text-secondary-20 dark:text-primary">
              {t("layout.admin_platform")}
            </h1>
            <button
              type="button"
              className="rounded-md p-2 text-secondary-20 hover:bg-surface-container-highest dark:text-primary dark:hover:text-secondary-50"
              onClick={toggleTheme}
              aria-label={
                theme === "dark"
                  ? "Switch to light mode"
                  : "Switch to dark mode"
              }
            >
              {theme === "dark" ? (
                <SunIcon className="h-6 w-6" aria-hidden="true" />
              ) : (
                <MoonIcon className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>

          <nav className="flex flex-1 flex-col justify-between">
            <ul className="space-y-1">
              {navigation.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={tw(
                      "group flex gap-x-3 border-l-3 border-transparent px-6 py-2 text-sm font-semibold leading-6 text-neutral-60 hover:text-secondary-20 dark:hover:text-primary",
                      current === item.href &&
                        "border-info-60 text-secondary-20 dark:border-info-70 dark:text-primary",
                    )}
                  >
                    <item.icon
                      className="h-6 w-6 shrink-0"
                      aria-hidden="true"
                    />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={logout}
              className={tw(
                "group flex gap-x-3 rounded-md px-6 py-2 text-sm font-semibold leading-6 text-neutral-60 hover:text-secondary-20 dark:hover:text-primary",
              )}
            >
              <LogoutIcon aria-hidden={true} />
              <span>{t("layout.logout")}</span>
            </button>
          </nav>
        </div>
      </div>
    </>
  );
};
